import {
    FirstPage,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    LastPage,
    FilterAlt,
    DeleteOutline,
} from "@mui/icons-material";
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Button,
    FormControl,
    Select,
    MenuItem,
} from "@mui/material";
import { DateTimePickerField } from "../components/DateTimePickerField";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import "./Table.css";

const formatDateFilter = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return { date: formatDateFilter(date), time: "00:00" };
};

const getDefaultEndDate = () => {
    return { date: formatDateFilter(new Date()), time: "23:59" };
};

const HistoryPage = () => {
    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;

    const [chatHistory, setChatHistory] = useState([]);
    const [page, setPage] = React.useState(0);

    const [startDatetimeFilter, setStartDatetimeFilter] =
        useState(getDefaultStartDate);
    const [endDatetimeFilter, setEndDatetimeFilter] =
        useState(getDefaultEndDate);
    const [idFilter, setIdFilter] = useState("");
    const [sourceFilter, setSourceFilter] = useState("");
    const [externalIdFilter, setExternalIdFilter] = useState("");

    const [appliedStartDatetimeFilter, setAppliedStartDatetimeFilter] =
        useState(() => {
            const d = getDefaultStartDate();
            const [day, month, year] = d.date.split("/");
            return `${year}-${month}-${day} ${d.time}:00`;
        });
    const [appliedEndDatetimeFilter, setAppliedEndDatetimeFilter] = useState(
        () => {
            const d = getDefaultEndDate();
            const [day, month, year] = d.date.split("/");
            return `${year}-${month}-${day} ${d.time}:59`;
        },
    );
    const [appliedIdFilter, setAppliedIdFilter] = useState("");
    const [appliedSourceFilter, setAppliedSourceFilter] = useState("");
    const [appliedExternalIdFilter, setAppliedExternalIdFilter] = useState("");

    const isFilterApplied =
        appliedStartDatetimeFilter !== "" ||
        appliedEndDatetimeFilter !== "" ||
        appliedIdFilter !== "" ||
        appliedSourceFilter !== "" ||
        appliedExternalIdFilter !== "";

    const rowsPerPage = 8;
    const start = page * rowsPerPage;
    const end = page * rowsPerPage + rowsPerPage;
    const pageContent = chatHistory.slice(start, end);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const toDatetimeStr = (filter, endOfDay = false) => {
        if (!filter?.date) return "";
        const [day, month, year] = filter.date.split("/");
        const formattedDate = `${year}-${month}-${day}`;
        if (filter.time) {
            return `${formattedDate} ${filter.time}:${endOfDay ? "59" : "00"}`;
        }
        return `${formattedDate} ${endOfDay ? "23:59:59" : "00:00:00"}`;
    };

    const handleSearch = () => {
        setAppliedStartDatetimeFilter(toDatetimeStr(startDatetimeFilter));
        setAppliedEndDatetimeFilter(toDatetimeStr(endDatetimeFilter, true));
        setAppliedIdFilter(idFilter);
        setAppliedSourceFilter(sourceFilter);
        setAppliedExternalIdFilter(externalIdFilter);
    };

    const handleClearFilters = () => {
        const defaultStart = getDefaultStartDate();
        const defaultEnd = getDefaultEndDate();

        setStartDatetimeFilter(defaultStart);
        setEndDatetimeFilter(defaultEnd);
        setIdFilter("");
        setSourceFilter("");
        setExternalIdFilter("");
        setAppliedStartDatetimeFilter(toDatetimeStr(defaultStart));
        setAppliedEndDatetimeFilter(toDatetimeStr(defaultEnd, true));
        setAppliedIdFilter("");
        setAppliedSourceFilter("");
        setAppliedExternalIdFilter("");
    };

    const sourcePtbr = {
        playground: "Playground",
        whatsapp: "WhatsApp",
        intercom: "Intercom",
        chiko_inbox: "Chiko Inbox",
        zendesk: "Zendesk",
    };

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const loadChatHistory = useCallback(async () => {
        let url = `${backendEndpoint}/chat_history/`;
        const params = new URLSearchParams();
        if (appliedStartDatetimeFilter) {
            params.append("start_datetime", appliedStartDatetimeFilter);
        }
        if (appliedEndDatetimeFilter) {
            params.append("end_datetime", appliedEndDatetimeFilter);
        }
        if (appliedIdFilter) params.append("session_id", appliedIdFilter);
        if (appliedSourceFilter) params.append("source", appliedSourceFilter);
        if (appliedExternalIdFilter)
            params.append("external_id", appliedExternalIdFilter);

        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        const response = await fetch(url, {
            method: "GET",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const json = await response.json();
        const chatHistory = json.chatHistory;

        setChatHistory(chatHistory);
        setPage(0);
    }, [
        backendEndpoint,
        getCookieValue,
        appliedStartDatetimeFilter,
        appliedEndDatetimeFilter,
        appliedIdFilter,
        appliedSourceFilter,
        appliedExternalIdFilter,
    ]);

    useEffect(() => {
        loadChatHistory();
    }, [loadChatHistory]);

    return (
        <Box
            sx={{ width: "100%", margin: "10px 15px 50px 15px" }}
            id="history-container"
        >
            <Box
                variant="h4"
                sx={{
                    width: "150",
                    color: "#333",
                    fontSize: "24px",
                    fontWeight: "500",
                }}
            >
                Histórico de Conversas
                <Box
                    variant="div"
                    sx={{
                        fontSize: "13px",
                        fontWeight: 400,
                        pr: "10px",
                    }}
                >
                    Todas as conversas realizadas pelo Chiko com seus usuários
                </Box>
            </Box>

            {chatHistory.length === 0 && !isFilterApplied ? (
                <Box sx={{ textAlign: "left", mt: 10 }}>
                    <Box sx={{ mb: 2 }}>
                        Nada por aqui por enquanto, o que acha de trocar uma
                        ideia com o Chiko?
                    </Box>

                    <Link href="/chat">
                        Clique aqui para acessar a página do chat
                    </Link>
                </Box>
            ) : (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "flex-end",
                            gap: 1.5,
                            mt: 3,
                            flexWrap: "wrap",
                            width: "100%",
                        }}
                    >
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                                Data Início
                            </Box>
                            <DateTimePickerField
                                value={startDatetimeFilter}
                                onChange={setStartDatetimeFilter}
                                placeholder="Data início..."
                                defaultTime="00:00"
                            />
                        </Box>

                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                                Data Fim
                            </Box>
                            <DateTimePickerField
                                value={endDatetimeFilter}
                                onChange={setEndDatetimeFilter}
                                placeholder="Data fim..."
                                defaultTime="23:59"
                            />
                        </Box>

                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                                ID
                            </Box>
                            <TextField
                                placeholder="Buscar por ID..."
                                variant="outlined"
                                size="small"
                                value={idFilter}
                                onChange={(e) => setIdFilter(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                                fullWidth
                                sx={{
                                    backgroundColor: "white",
                                    borderRadius: "4px",
                                }}
                            />
                        </Box>

                        <Box sx={{ minWidth: 150, flex: 1 }}>
                            <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                                Origem
                            </Box>
                            <FormControl
                                fullWidth
                                size="small"
                                sx={{
                                    backgroundColor: "white",
                                    borderRadius: "4px",
                                }}
                            >
                                <Select
                                    value={sourceFilter}
                                    onChange={(e) =>
                                        setSourceFilter(e.target.value)
                                    }
                                    displayEmpty
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {Object.entries(sourcePtbr).map(
                                        ([key, label]) => (
                                            <MenuItem key={key} value={key}>
                                                {label}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ minWidth: 150, flex: 1 }}>
                            <Box sx={{ fontSize: 13, mb: 0.5, color: "#777" }}>
                                ID externo
                            </Box>
                            <TextField
                                placeholder="Buscar ID externo..."
                                variant="outlined"
                                size="small"
                                value={externalIdFilter}
                                onChange={(e) =>
                                    setExternalIdFilter(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                                fullWidth
                                sx={{
                                    backgroundColor: "white",
                                    borderRadius: "4px",
                                }}
                            />
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<FilterAlt />}
                            onClick={handleSearch}
                            sx={{
                                borderRadius: "25px",
                                padding: "8px 16px 6px",
                                textTransform: "none",
                                mb: 0.2,
                            }}
                        >
                            Filtrar
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DeleteOutline />}
                            onClick={handleClearFilters}
                            disabled={!isFilterApplied}
                            sx={{
                                borderRadius: "25px",
                                padding: "8px 16px 6px",
                                textTransform: "none",
                                mb: 0.2,
                            }}
                        >
                            Limpar
                        </Button>
                    </Box>

                    <TableContainer
                        sx={{
                            position: "relative",
                            borderRadius: 1,

                            mt: 2,
                        }}
                    >
                        <Table
                            sx={{ border: "1px solid rgba(224, 224, 224, 1)" }}
                        >
                            <TableHead
                                sx={{
                                    backgroundColor: "#E2E8F0",
                                    fontWeight: "500",
                                }}
                            >
                                <TableRow>
                                    <TableCell>Criado em</TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Origem</TableCell>
                                    <TableCell>ID externo</TableCell>
                                    {/*
                                                <TableCell sx={{ width: "1px" }}>
                                                    Ação
                                                </TableCell>
                                                */}
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    backgroundColor: "white",
                                    fontWeight: "500",
                                }}
                            >
                                {chatHistory.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            sx={{
                                                textAlign: "center",
                                                py: 4,
                                                color: "#777",
                                            }}
                                        >
                                            Nenhuma conversa encontrada para os
                                            filtros selecionados.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pageContent.map((content, index) => (
                                        <TableRow
                                            key={index}
                                            onClick={() =>
                                                window.open(
                                                    `/history/${content.id}`,
                                                    "_self",
                                                )
                                            }
                                        >
                                            <TableCell>
                                                {content.createdOn}
                                            </TableCell>
                                            <TableCell>{content.id}</TableCell>
                                            <TableCell>
                                                {content.source}
                                            </TableCell>
                                            <TableCell>
                                                {content.externalId}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                            <TableFooter
                                sx={{
                                    backgroundColor: "#E2E8F0",
                                }}
                            >
                                <TableRow>
                                    <TablePagination
                                        rowsPerPageOptions={[rowsPerPage]}
                                        count={chatHistory.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        ActionsComponent={
                                            TablePaginationActions
                                        }
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

function TablePaginationActions(props) {
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                <FirstPage />
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                <KeyboardArrowLeft />
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                <KeyboardArrowRight />
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                <LastPage />
            </IconButton>
        </Box>
    );
}

export default HistoryPage;
